/**
 * notificationPrompt — lógica PURA do convite "Receber notificações?"
 * (Story X1.2.2).
 *
 * Zero dependência de React/DOM/Notification API → testável no vitest sem mocks
 * (mesmo padrão de lib/pwaInstall.ts). O hook useNotificationPermission injeta
 * `window.localStorage` e os sinais externos (permission, suporte, subscription);
 * aqui ficam só as decisões e a persistência via KeyValueStore.
 *
 * Decisões da story:
 * - Convite só aparece na 3ª visita (NOTIF_PROMPT_MIN_VISITS) — não incomoda
 *   o usuário logo de cara, espera ele engajar.
 * - Só quando `permission === "default"`: em "granted" já está ativo; em
 *   "denied" o browser bloqueia re-pedir → mostrar viraria nag inútil.
 * - "Agora não" persiste por 30 dias (mais longo que o install banner de 7d:
 *   recusar notificação é um sinal mais forte de desinteresse).
 */

export const NOTIF_PROMPT_VISITS_KEY = "seweb:notif:visits";
export const NOTIF_PROMPT_DISMISS_KEY = "seweb:notif:prompt-dismissed-until";
export const NOTIF_PROMPT_MIN_VISITS = 3;
export const NOTIF_PROMPT_DISMISS_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Interface mínima de storage (localStorage em prod; objeto fake nos testes). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Estado de permissão de notificação (espelha Notification.permission). */
export type NotifPermission = "default" | "granted" | "denied";

/**
 * Lê o contador de visitas. Valor ausente/corrompido (NaN, negativo) → 0.
 */
export function getVisitCount(store: KeyValueStore): number {
  const raw = store.getItem(NOTIF_PROMPT_VISITS_KEY);
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** Incrementa e persiste o contador de visitas; retorna o novo valor. */
export function recordVisit(store: KeyValueStore): number {
  const next = getVisitCount(store) + 1;
  store.setItem(NOTIF_PROMPT_VISITS_KEY, String(next));
  return next;
}

/** Grava o timestamp (now + dias) até o qual o convite fica oculto. */
export function setPromptDismissed(
  store: KeyValueStore,
  now: number,
  days: number = NOTIF_PROMPT_DISMISS_DAYS
): void {
  store.setItem(NOTIF_PROMPT_DISMISS_KEY, String(now + days * MS_PER_DAY));
}

/** True ⇔ ainda estamos dentro da janela de dismiss (convite deve ficar oculto). */
export function isPromptDismissActive(store: KeyValueStore, now: number): boolean {
  const raw = store.getItem(NOTIF_PROMPT_DISMISS_KEY);
  if (!raw) return false;
  const until = Number(raw);
  // Valor corrompido (NaN) → trata como não-dismissado (fail-open pro convite).
  if (!Number.isFinite(until)) return false;
  return now < until;
}

export interface ShouldShowPromptInput {
  /** SW + Push API + Notification API disponíveis no navegador. */
  isSupported: boolean;
  /** Estado atual de Notification.permission. */
  permission: NotifPermission;
  /** Quantas vezes o app já foi carregado por este usuário. */
  visitCount: number;
  /** Dentro da janela de dismiss de 30d. */
  dismissActive: boolean;
  /** Já existe uma subscription ativa (não faz sentido convidar de novo). */
  alreadySubscribed: boolean;
  /** O SERVIDOR tem chave VAPID configurada?
   *
   * `undefined` = ainda não sabemos (a checagem é assíncrona) — nesse caso não
   * convidamos, porque perguntar cedo demais e descobrir depois que não dá para
   * enviar é o pior dos mundos.
   *
   * Sem VAPID o canal PUSH degrada em SILÊNCIO (`web_push_service.py:96`).
   * Pedir permissão de notificação e nunca enviar nada é pior do que não pedir:
   * gasta a única chance de perguntar (o navegador não repete o convite depois
   * de um "bloquear") e ensina o usuário que o pedido era inútil. */
  serverHasVapid?: boolean;
  /** A intenção gravada neste aparelho é `ligado` (`lib/push/memoriaDoPush.ts`)? A pessoa já ligou o push aqui, então perguntar de novo
   * "Receber notificações?" seria um convite a quem já aceitou: se o push parou, quem fala é o `AvisoDoPush` (plano "Push: religar
   * sozinho ou avisar", Decisão 8). `undefined` = sem intenção conhecida (convida como antes). */
  intencaoLigada?: boolean;
}

/**
 * Decisão central: o convite deve aparecer?
 *
 * Suprime quando: navegador sem suporte, permissão ≠ default, intenção `ligado`
 * neste aparelho, já subscrito, ou dismiss ativo. Caso contrário, mostra a partir
 * da 3ª visita.
 */
export function shouldShowPrompt(input: ShouldShowPromptInput): boolean {
  const { isSupported, permission, visitCount, dismissActive, alreadySubscribed } = input;
  if (!isSupported) return false;
  // Ver o comentário de `serverHasVapid`: só convida quando dá para cumprir.
  if (input.serverHasVapid !== true) return false;
  if (permission !== "default") return false;
  // O aviso toma o lugar do convite em aparelho que já ligou o push (ver `intencaoLigada`).
  if (input.intencaoLigada === true) return false;
  if (alreadySubscribed) return false;
  if (dismissActive) return false;
  return visitCount >= NOTIF_PROMPT_MIN_VISITS;
}
